"use client";

import React from "react";
import SHeading from "@/components/atoms/SHeading";
import MemberCard from "@/components/molecules/MemberCard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useTranslation } from "react-i18next";
import {
    IMG_GIAM_DOC_DIEU_HANH,
    IMG_GIAM_DOC_KHACH_HANG,
    IMG_GIAM_DOC_DU_AN,
    IMG_GIAM_DOC_KY_THUAT,
    IMG_GIAM_DOC_VAN_HANH,
    IMG_NGUYEN_THU_TRUNG,
    IMG_PHAN_HUY_CONG,
    IMG_NGUYEN_VIET_ANH,
    IMG_NGUYEN_TRUNG_SON,
    IMG_VU_THANH_TUNG,
    IMG_VAN_DUC_HAI_TRUNG,
    IMG_NGUYEN_TRONG_DANH,
    IMG_DO_THANH_HIEN,
    IMG_PHI_HUYEN_TRANG,
} from "@/constants/imagesUrl";

interface TeamMember {
    name: string;
    position: string;
}

const OurTeams = () => {
    const { t } = useTranslation();

    const images: Record<"row1" | "row2" | "row3" | "row4", string[]> = {
        row1: [IMG_GIAM_DOC_DIEU_HANH, IMG_GIAM_DOC_KHACH_HANG],
        row2: [IMG_GIAM_DOC_DU_AN, IMG_GIAM_DOC_KY_THUAT, IMG_GIAM_DOC_VAN_HANH],
        row3: [
            IMG_NGUYEN_THU_TRUNG,
            IMG_PHAN_HUY_CONG,
            IMG_NGUYEN_VIET_ANH,
            IMG_NGUYEN_TRUNG_SON,
        ],
        row4: [
            IMG_VU_THANH_TUNG,
            IMG_VAN_DUC_HAI_TRUNG,
            IMG_NGUYEN_TRONG_DANH,
            IMG_DO_THANH_HIEN,
            IMG_PHI_HUYEN_TRANG,
        ],
    };

    const getImageForMember = (row: keyof typeof images, index: number): string => {
        return images[row]?.[index] || "";
    };

    const teamMembers = t("team_members", { returnObjects: true }) as Record<string, TeamMember[]>;

    const renderMemberGrid = (membersData: Record<string, TeamMember[]>, startIndex: number, endIndex: number) => {
        const rows = Object.keys(membersData).slice(startIndex, endIndex);

        return (
            <>
                <div className="hidden md:block max-w-7xl mx-auto">
                    {rows.map((rowKey) => {
                        const row = membersData[rowKey];
                        const totalMembers = row.length;
                        const gridCols =
                            totalMembers <= 2
                                ? "grid-cols-2 max-w-lg"
                                : totalMembers <= 3
                                    ? "grid-cols-3 max-w-4xl"
                                    : totalMembers <= 4
                                        ? "grid-cols-4 max-w-5xl"
                                        : "grid-cols-5 max-w-7xl";

                        return (
                            <div
                                key={`row-${rowKey}`}
                                className={`grid gap-8 justify-items-center mb-4 ${gridCols} mx-auto`}
                            >
                                {row.map((member, memberIndex) => (
                                    <MemberCard
                                        key={`member-${rowKey}-${memberIndex}`}
                                        image={getImageForMember(rowKey as keyof typeof images, memberIndex)}
                                        name={member.name}
                                        position={member.position}
                                    />
                                ))}
                            </div>
                        );
                    })}
                </div>

                <div className="md:hidden w-full">
                    <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        navigation
                        pagination={{ clickable: true }}
                        autoplay={{
                            delay: 2000,
                            disableOnInteraction: false,
                        }}
                        spaceBetween={20}
                        slidesPerView={1}
                        className="custom-swiper"
                    >
                        {rows.flatMap((rowKey) =>
                            membersData[rowKey].map((member, memberIndex) => (
                                <SwiperSlide key={`slide-${rowKey}-${memberIndex}`}>
                                    <div className="flex justify-center pb-4">
                                        <MemberCard
                                            image={getImageForMember(rowKey as keyof typeof images, memberIndex)}
                                            name={member.name}
                                            position={member.position}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))
                        )}
                    </Swiper>
                </div>
            </>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 mb-16">
            <SHeading
                text={t("home.our_team")}
                tAlign="center"
                className="font-bold md:text-3xl text-xl mb-8"
            />
            <div className="flex flex-col items-center gap-4">
                <SHeading
                    text={t("home.leadership_team")}
                    tAlign="center"
                    className="font-bold md:text-xl text-lg"
                />
                {renderMemberGrid(teamMembers, 0, 2)}

                <SHeading
                    text={t("home.development_team")}
                    tAlign="center"
                    className="font-bold md:text-xl text-lg"
                />
                {renderMemberGrid(teamMembers, 2, 4)}
            </div>
        </div>
    );
};

export default OurTeams;
